import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

import KendoInput from "assets/theme/components/inputKendo";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { cc } from "afms/theme/commandCenterTheme";
import { groupByOrg } from "utils/recipientGroups";

const OTHER = "__other__";

const FieldLabel = ({ children, required }) => (
  <SoftTypography
    variant="caption"
    fontWeight="bold"
    sx={{ display: "block", mb: 0.5, letterSpacing: 0.3, color: cc.ink }}
  >
    {children}
    {required && <span style={{ color: cc.red, marginLeft: 4 }}>*</span>}
  </SoftTypography>
);

FieldLabel.propTypes = { children: PropTypes.node.isRequired, required: PropTypes.bool };

// Emails the quote and moves it to Sent. Recipients come from the quote's own
// customer organisation (GET /admin/quotations/{id}/recipients) so the seller
// picks a real contact rather than retyping an address — "Someone else" is
// there for a one-off.
function SendQuotationModal({ open, onClose, onSent, quote }) {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState("");
  const [otherEmail, setOtherEmail] = useState("");
  const [message, setMessage] = useState("");
  // Off by default — most quotes go out for approval, not immediate payment.
  const [embedPayOnline, setEmbedPayOnline] = useState(false);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !quote?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setPicked("");
        setOtherEmail("");
        setMessage("");
        setEmbedPayOnline(false);
        setAttachPdf(true);
        const response = await AdminQuotationApiService.getRecipients(quote.id);
        if (cancelled) return;
        const info = response?.data?.data ?? null;
        const list = Array.isArray(info?.recipients) ? info.recipients : [];
        setRecipients(list);
        // One obvious contact — preselect it rather than making them choose.
        // Prefer a lone contact at the org actually being billed now that the
        // list spans every client org.
        const atCustomer = list.filter((r) => r.isQuoteCustomer);
        const obvious = atCustomer.length === 1 ? atCustomer : list;
        if (obvious.length === 1) setPicked(String(obvious[0].userProfileId));
      } catch (error) {
        if (cancelled) return;
        toast.error(error?.response?.data?.message || "Failed to load contacts");
        setRecipients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, quote?.id]);

  const recipientGroups = useMemo(() => groupByOrg(recipients), [recipients]);

  const handleSend = async () => {
    if (sending) return;
    if (!picked) {
      toast.error("Pick who this goes to.");
      return;
    }
    if (picked === OTHER && !otherEmail.trim()) {
      toast.error("Enter an email address.");
      return;
    }
    try {
      setSending(true);
      await AdminQuotationApiService.sendQuotation(quote.id, {
        userProfileId: picked === OTHER ? null : Number(picked),
        email: picked === OTHER ? otherEmail.trim() : null,
        message: message.trim() || null,
        embedPayOnline,
        attachPdf,
      });
      toast.success("Quotation sent");
      onSent?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: cc.ink }}>
          Send Quote {quote?.id ? `#${quote.id}` : ""}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          Emails the quote and marks it Sent.
        </SoftTypography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={sending}
          sx={{ position: "absolute", right: 8, top: 8, color: cc.muted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: cc.page }}>
        {loading ? (
          <Skeleton variant="rectangular" height={140} animation="wave" />
        ) : (
          <>
            <FieldLabel required>To</FieldLabel>
            {recipients.length === 0 && (
              <SoftTypography variant="caption" color="text" display="block" mb={1}>
                No contacts on file for {quote?.customerName || "this customer"} — enter an
                address below.
              </SoftTypography>
            )}
            <select
              className="custom-select"
              name="recipient"
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
            >
              <option value="">Select a recipient</option>
              {recipientGroups.map((group) => (
                <optgroup
                  key={group.id}
                  label={group.isQuoteCustomer ? `${group.name} (billed)` : group.name}
                >
                  {group.people.map((r) => (
                    <option value={String(r.userProfileId)} key={r.userProfileId}>
                      {r.name ? `${r.name} — ${r.email}` : r.email}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value={OTHER}>Someone else…</option>
            </select>

            {picked === OTHER && (
              <Box mt={1.5}>
                <FieldLabel required>Email address</FieldLabel>
                <KendoInput
                  type="email"
                  placeholder="name@company.com"
                  name="otherEmail"
                  value={otherEmail}
                  onChange={(e) => setOtherEmail(e.target.value)}
                />
              </Box>
            )}

            <Box mt={2}>
              <FieldLabel>Message (optional)</FieldLabel>
              <KendoInput
                placeholder="A short note to include above the quote"
                name="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Default-size Switch on purpose — size="small" leaves the thumb
                off-centre under the SoftUI theme. */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Switch
                checked={attachPdf}
                onChange={(e) => setAttachPdf(e.target.checked)}
                inputProps={{ "aria-label": "Attach the quote as a PDF" }}
              />
              <Box>
                <SoftTypography variant="button" fontWeight="medium" sx={{ color: cc.ink }}>
                  Attach quote as PDF
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block">
                  The same document the download action produces.
                </SoftTypography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5} mt={1}>
              <Switch
                checked={embedPayOnline}
                onChange={(e) => setEmbedPayOnline(e.target.checked)}
                inputProps={{ "aria-label": "Embed a Pay Online button" }}
              />
              <Box>
                <SoftTypography variant="button" fontWeight="medium" sx={{ color: cc.ink }}>
                  Embed &ldquo;Pay Online&rdquo; button
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block">
                  Adds a checkout link so the customer can pay straight from the email.
                </SoftTypography>
              </Box>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <SoftButton
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onClose}
          disabled={sending}
        >
          Cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="info"
          size="small"
          onClick={handleSend}
          disabled={sending || loading}
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {sending ? "Sending…" : "Send Quote"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

SendQuotationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func,
  quote: PropTypes.object,
};

SendQuotationModal.defaultProps = { onSent: undefined, quote: null };

export default SendQuotationModal;
